// frontend/src/utils/publicAi.js
//
// The logged-out AI tries on the name-generator pages (Growth round, T22).
//
// THE BROWSER ID is a random id kept in localStorage. The server allows one
// free AI try per visitor per day and counts a visitor by IP address AND by
// this id (backend/services/publicAiLimits.js). It identifies a browser, not
// a person, and is only sent to our own /api.
//
// Logged-in visitors are recognised by their session cookie, so every call
// sends credentials; the server then spends their AI credits instead.

export const BROWSER_ID_KEY = 'gs_browser_id'
const VALID_ID = /^[A-Za-z0-9_-]{8,64}$/

function randomId () {
  const bytes = new Uint8Array(12)
  const c = typeof window !== 'undefined' && window.crypto
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(bytes)
  else for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256)
  return 'gs-' + Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
}

// Kept for the session when storage is blocked (private mode).
let memoryId = null

export function getBrowserId () {
  try {
    const stored = window.localStorage.getItem(BROWSER_ID_KEY)
    if (stored && VALID_ID.test(stored)) return stored
    const id = randomId()
    window.localStorage.setItem(BROWSER_ID_KEY, id)
    return id
  } catch {
    memoryId = memoryId || randomId()
    return memoryId
  }
}

/** Which platform's handle rules the AI names follow on a generator page. */
export function vibePlatformFor (cfg) {
  if (cfg && cfg.bank === 'tiktok') return 'tiktok'
  if (cfg && cfg.bank === 'youtube') return 'youtube'
  return 'instagram'
}

export const VIBE_MAX = 120

/**
 * POST /api/public/ai/vibe-names.
 * Resolves to { ok: true, data } or { ok: false, status, error, message };
 * never throws (status 0 = network failure).
 */
export async function fetchVibeNames ({ vibe, platform }, fetchImpl) {
  const doFetch = fetchImpl || (typeof fetch === 'function' ? fetch : null)
  if (!doFetch) return { ok: false, status: 0, error: 'offline', message: '' }
  try {
    const r = await doFetch('/api/public/ai/vibe-names', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ vibe: String(vibe || '').slice(0, VIBE_MAX), platform, browserId: getBrowserId() }),
    })
    let data = null
    try { data = await r.json() } catch { data = null }
    if (r.ok && data && Array.isArray(data.names)) return { ok: true, data }
    return { ok: false, status: r.status, error: (data && data.error) || '', message: (data && data.message) || '' }
  } catch {
    return { ok: false, status: 0, error: 'offline', message: '' }
  }
}

/* ── AI profile kit (T23) ─────────────────────────────────────────────────── */

export const LAST_KIT_KEY = 'gs_profile_kit'

/**
 * POST /api/public/ai/profile-kit. Logged out the answer holds bio 1 and
 * only the COUNTS of the locked parts (`locked`); logged in, the whole kit.
 * Resolves to { ok: true, data } or { ok: false, status, error, message }.
 */
export async function fetchProfileKit ({ name, style, platform, vibe }, fetchImpl) {
  const doFetch = fetchImpl || (typeof fetch === 'function' ? fetch : null)
  if (!doFetch) return { ok: false, status: 0, error: 'offline', message: '' }
  try {
    const r = await doFetch('/api/public/ai/profile-kit', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name, style, platform, browserId: getBrowserId(),
        ...(vibe ? { vibe: String(vibe).slice(0, VIBE_MAX) } : {}),
      }),
    })
    let data = null
    try { data = await r.json() } catch { data = null }
    if (r.ok && data && data.kitId && data.bio) return { ok: true, data }
    return { ok: false, status: r.status, error: (data && data.error) || '', message: (data && data.message) || '' }
  } catch {
    return { ok: false, status: 0, error: 'offline', message: '' }
  }
}

/**
 * The last kit this browser made, so a reload shows it again instead of
 * spending another try. Only what the server sent is kept.
 */
export function readLastKit (name, platform) {
  try {
    const kit = JSON.parse(window.localStorage.getItem(LAST_KIT_KEY) || 'null')
    if (!kit || !kit.kitId || !kit.bio) return null
    if (name && String(kit.name).toLowerCase() !== String(name).toLowerCase()) return null
    if (platform && kit.platform && kit.platform !== platform) return null
    if (kit.expiresAt && Date.parse(kit.expiresAt) < Date.now()) return null
    return kit
  } catch { return null }
}

export function saveLastKit (kit) {
  try { window.localStorage.setItem(LAST_KIT_KEY, JSON.stringify(kit)) } catch { /* storage blocked */ }
}
